(() => {
  if (window.__purefectMobileAppLoaded) return;
  window.__purefectMobileAppLoaded = true;

  const phoneQuery = window.matchMedia('(max-width: 1024px)');
  const userAgent = navigator.userAgent || '';
  const touchMacTablet = navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1;
  const tabletDevice =
    /iPad|Tablet/i.test(userAgent) ||
    (/Android/i.test(userAgent) && !/Mobile/i.test(userAgent)) ||
    touchMacTablet;
  const compactScreen = Math.min(
    window.screen?.width || window.innerWidth,
    window.screen?.height || window.innerHeight
  ) <= 600;
  const phoneDevice = /iPhone|iPod|Android.*Mobile|Mobile/i.test(userAgent) || compactScreen;
  const shouldUsePhoneApp = () => phoneQuery.matches && phoneDevice && !tabletDevice;
  const shell = document.querySelector('[data-pm-mobile-app-shell]');
  if (!shell) return;

  let teardown = [];
  let activeDialog = null;
  let searchAbortController = null;
  let searchTimer = 0;
  let toastTimer = 0;
  let returnRestoreCleanup = null;

  const returnStorageKey = 'purefect:mobile-return:v2';
  const pendingReturnStorageKey = 'purefect:mobile-return-pending:v2';
  const returnStateMaxAge = 2 * 60 * 60 * 1000;
  const menuAnchorStorageKey = 'purefect:mobile-menu-anchor:v2';
  const menuAnchorMaxAge = 60 * 1000;

  const on = (target, eventName, listener, options) => {
    target?.addEventListener(eventName, listener, options);
    teardown.push(() => target?.removeEventListener(eventName, listener, options));
  };

  const emitToast = (message) => {
    const toast = shell.querySelector('[data-pm-mobile-toast]');
    if (!toast || !message) return;
    window.clearTimeout(toastTimer);
    toast.textContent = String(message);
    toast.hidden = false;
    toastTimer = window.setTimeout(() => {
      toast.hidden = true;
      toast.textContent = '';
    }, 2800);
  };

  const releaseDialogState = (dialog) => {
    if (activeDialog === dialog) activeDialog = null;
    if (!document.querySelector('.pm-mobile-sheet[open]')) {
      document.body.classList.remove('pm-mobile-sheet-open');
    }
  };

  const closeDialog = (dialog, useHistory = true) => {
    if (!dialog?.open) return;
    const id = dialog.id;
    dialog.close();
    releaseDialogState(dialog);
    if (useHistory && window.history.state?.pmMobileDialog === id) {
      window.history.back();
    }
  };

  const openDialog = (dialog, returnFocus, pushHistory = true) => {
    if (!dialog || dialog.open) return;
    if (activeDialog?.open) closeDialog(activeDialog, false);
    dialog.pmMobileReturnFocus = returnFocus || document.activeElement;
    if (typeof dialog.showModal !== 'function') {
      const fallbackUrl = returnFocus?.dataset?.fallbackUrl;
      if (fallbackUrl) window.location.assign(fallbackUrl);
      else emitToast('Open this tool from the full menu.');
      return;
    }
    try {
      dialog.showModal();
    } catch (error) {
      const fallbackUrl = returnFocus?.dataset?.fallbackUrl;
      if (fallbackUrl) window.location.assign(fallbackUrl);
      else emitToast('Open this tool from the full menu.');
      return;
    }
    activeDialog = dialog;
    document.body.classList.add('pm-mobile-sheet-open');
    returnFocus?.setAttribute('aria-expanded', 'true');
    if (pushHistory) {
      const currentState = window.history.state && typeof window.history.state === 'object' ? window.history.state : {};
      window.history.pushState({ ...currentState, pmMobileDialog: dialog.id }, '');
    }
    if (dialog.matches('[data-pm-mobile-dialog="search"]')) {
      window.setTimeout(() => dialog.querySelector('[data-pm-mobile-search-input]')?.focus(), 60);
    } else {
      window.setTimeout(() => dialog.querySelector('button, a, input')?.focus(), 40);
    }
  };

  const bridgeTo = (selector, source) => {
    const destination = document.querySelector(selector);
    if (!destination) {
      emitToast('That tool is not available on this page yet.');
      return;
    }
    const containingDialog = source?.closest('.pm-mobile-sheet');
    if (containingDialog?.open) {
      closeDialog(containingDialog);
      window.setTimeout(() => destination.click(), 80);
      return;
    }
    destination.click();
  };

  const configureDialogs = () => {
    shell.querySelectorAll('.pm-mobile-sheet').forEach((dialog) => {
      const cancelHandler = (event) => {
        event.preventDefault();
        closeDialog(dialog);
      };
      const backdropHandler = (event) => {
        if (event.target === dialog && dialog.matches('.pm-mobile-sheet--more')) closeDialog(dialog);
      };
      const closeHandler = () => {
        releaseDialogState(dialog);
        const target = dialog.pmMobileReturnFocus;
        target?.setAttribute?.('aria-expanded', 'false');
        if (target instanceof HTMLElement && target.isConnected) {
          window.setTimeout(() => target.focus({ preventScroll: true }), 0);
        }
      };
      on(dialog, 'cancel', cancelHandler);
      on(dialog, 'click', backdropHandler);
      on(dialog, 'close', closeHandler);
    });

    const popstateHandler = (event) => {
      const requestedId = event.state?.pmMobileDialog;
      const requestedDialog = requestedId ? document.getElementById(requestedId) : null;
      if (requestedDialog && shouldUsePhoneApp() && !requestedDialog.open) {
        const trigger = shell.querySelector('[aria-controls="' + CSS.escape(requestedId) + '"]');
        openDialog(requestedDialog, trigger, false);
        return;
      }
      if (activeDialog?.open) {
        const dialog = activeDialog;
        dialog.close();
        releaseDialogState(dialog);
      }
    };
    on(window, 'popstate', popstateHandler);

    const requestedId = window.history.state?.pmMobileDialog;
    const requestedDialog = requestedId ? document.getElementById(requestedId) : null;
    if (requestedDialog && shouldUsePhoneApp() && !requestedDialog.open) {
      const trigger = shell.querySelector('[aria-controls="' + CSS.escape(requestedId) + '"]');
      window.requestAnimationFrame(() => openDialog(requestedDialog, trigger, false));
    }
  };

  const productHandleFromURL = (value) => {
    try {
      const url = new URL(value, window.location.origin);
      const match = url.pathname.match(/\/products\/([^/?#]+)/i);
      return match ? decodeURIComponent(match[1]) : '';
    } catch (error) {
      return '';
    }
  };

  const currentRelativeURL = () =>
    window.location.pathname + window.location.search + window.location.hash;

  const isFreshReturnState = (snapshot) =>
    Boolean(
      snapshot &&
        snapshot.version === 2 &&
        snapshot.sourceUrl &&
        Number.isFinite(Number(snapshot.scrollY)) &&
        Date.now() - Number(snapshot.timestamp || 0) < returnStateMaxAge
    );

  const readStoredReturn = (key = returnStorageKey) => {
    try {
      const snapshot = JSON.parse(window.sessionStorage.getItem(key) || 'null');
      return isFreshReturnState(snapshot) ? snapshot : null;
    } catch (error) {
      return null;
    }
  };

  const writeStoredReturn = (key, snapshot) => {
    try {
      window.sessionStorage.setItem(key, JSON.stringify(snapshot));
    } catch (error) {
      // History state remains available when private browsing blocks storage.
    }
  };

  const buildReturnLocator = (anchor, destinationHandle) => {
    const card =
      anchor.closest('[data-pm-product-card]') ||
      anchor.closest('#product-grid > [data-pm-product-title]');
    if (card) {
      const handle = card.dataset.productHandle || destinationHandle;
      const matchingCards = handle
        ? Array.from(document.querySelectorAll('[data-pm-product-card][data-product-handle]')).filter(
            (candidate) => candidate.dataset.productHandle === handle
          )
        : [];
      return {
        kind: 'product-card',
        handle,
        ordinal: Math.max(0, matchingCards.indexOf(card)),
        linkId: anchor.id || card.querySelector('a[id^="CardLink-"]')?.id || ''
      };
    }

    const gallerySlide = anchor.closest('[data-gallery-slide][data-slide-index]');
    if (gallerySlide) {
      return {
        kind: 'menu-gallery',
        index: gallerySlide.dataset.slideIndex || ''
      };
    }

    const weeklyFavorite = anchor.closest('[id^="PmWeeklyFavorite-"]');
    if (weeklyFavorite) {
      return {
        kind: 'weekly-favorite',
        id: weeklyFavorite.id
      };
    }

    return {
      kind: 'link',
      id: anchor.id || ''
    };
  };

  const resolveReturnLocator = (snapshot) => {
    const locator = snapshot?.locator;
    if (!locator) return null;

    if (locator.linkId) {
      const link = document.getElementById(locator.linkId);
      if (link) return link.closest('[data-pm-product-card], [data-gallery-slide]') || link;
    }

    if (locator.kind === 'product-card' && locator.handle) {
      const cards = Array.from(
        document.querySelectorAll('[data-pm-product-card][data-product-handle]')
      ).filter((candidate) => candidate.dataset.productHandle === locator.handle);
      return cards[Number(locator.ordinal) || 0] || cards[0] || null;
    }

    if (locator.kind === 'menu-gallery') {
      return (
        Array.from(document.querySelectorAll('[data-gallery-slide][data-slide-index]')).find(
          (slide) => slide.dataset.slideIndex === String(locator.index)
        ) || null
      );
    }

    if (locator.kind === 'weekly-favorite' && locator.id) {
      return document.getElementById(locator.id);
    }

    if (locator.kind === 'link' && locator.id) return document.getElementById(locator.id);
    return null;
  };

  const captureProductReturnState = (event) => {
    if (!shouldUsePhoneApp() || event.defaultPrevented || event.button !== 0) return;
    if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
    const target = event.target instanceof Element ? event.target : event.target?.parentElement;
    const anchor = target?.closest?.('a[href]');
    if (!anchor || anchor.hasAttribute('download')) return;
    if (anchor.target && anchor.target.toLowerCase() !== '_self') return;

    let destination;
    try {
      destination = new URL(anchor.href, window.location.origin);
    } catch (error) {
      return;
    }
    const destinationHandle = productHandleFromURL(destination);
    if (destination.origin !== window.location.origin || !destinationHandle) return;

    const locator = buildReturnLocator(anchor, destinationHandle);
    const anchoredElement =
      anchor.closest('[data-pm-product-card], [data-gallery-slide], [id^="PmWeeklyFavorite-"]') ||
      anchor;
    const menuRoot = document.querySelector('[data-pm-menu-collection]');
    const searchInput = document.querySelector('purefect-menu-search input[type="search"]');
    const snapshot = {
      version: 2,
      sourceUrl: currentRelativeURL(),
      destinationHandle,
      scrollY: Math.max(0, window.scrollY),
      anchorTop: anchoredElement.getBoundingClientRect().top,
      locator,
      searchValue: searchInput?.value || '',
      menuView: menuRoot?.dataset.pmMobileView || '',
      timestamp: Date.now()
    };

    const currentState =
      window.history.state && typeof window.history.state === 'object'
        ? window.history.state
        : {};
    try {
      window.history.replaceState(
        { ...currentState, pmMobileReturn: snapshot },
        '',
        window.location.href
      );
    } catch (error) {
      // sessionStorage still provides a same-tab fallback.
    }
    writeStoredReturn(returnStorageKey, snapshot);
  };

  const returnSnapshotForCurrentProduct = () => {
    const snapshot = readStoredReturn();
    const handle = productHandleFromURL(window.location.href);
    return snapshot && snapshot.destinationHandle === handle ? snapshot : null;
  };

  const restoreCapturedPosition = () => {
    if (!shouldUsePhoneApp()) return;
    returnRestoreCleanup?.();
    returnRestoreCleanup = null;

    const pendingSnapshot = readStoredReturn(pendingReturnStorageKey);
    const historySnapshot = window.history.state?.pmMobileReturn;
    const snapshot = isFreshReturnState(pendingSnapshot)
      ? pendingSnapshot
      : isFreshReturnState(historySnapshot)
        ? historySnapshot
        : null;
    if (!snapshot) return;

    let expectedSource;
    try {
      expectedSource = new URL(snapshot.sourceUrl, window.location.origin);
    } catch (error) {
      return;
    }
    if (
      expectedSource.origin !== window.location.origin ||
      expectedSource.pathname + expectedSource.search + expectedSource.hash !== currentRelativeURL()
    ) {
      return;
    }

    try {
      window.sessionStorage.removeItem(pendingReturnStorageKey);
    } catch (error) {
      // A stale pending flag is harmless because URL and age are validated.
    }

    const viewController = document.querySelector('purefect-grid-view');
    const menuRoot = document.querySelector('[data-pm-menu-collection]');
    if (snapshot.menuView === 'grid' || snapshot.menuView === 'list') {
      if (typeof viewController?.setView === 'function') {
        viewController.setView(snapshot.menuView, false);
      } else if (menuRoot) {
        menuRoot.dataset.pmMobileView = snapshot.menuView;
      }
    }

    const searchInput = document.querySelector('purefect-menu-search input[type="search"]');
    if (searchInput && searchInput.value !== snapshot.searchValue) {
      searchInput.value = snapshot.searchValue || '';
      searchInput.dispatchEvent(new Event('input', { bubbles: true }));
    }

    let cancelled = false;
    let focusTimer = 0;
    let endTimer = 0;
    let mutationFrame = 0;
    const timers = [];
    const cancellationEvents = ['touchstart', 'pointerdown', 'wheel', 'keydown'];

    const align = () => {
      if (cancelled) return;
      const anchoredElement = resolveReturnLocator(snapshot);
      if (anchoredElement && anchoredElement.getClientRects().length) {
        const delta = anchoredElement.getBoundingClientRect().top - Number(snapshot.anchorTop || 0);
        if (Math.abs(delta) > 1) window.scrollBy(0, delta);
      } else {
        window.scrollTo(0, Number(snapshot.scrollY) || 0);
      }
    };

    const cancel = () => {
      cancelled = true;
      returnRestoreCleanup?.();
      returnRestoreCleanup = null;
    };

    const observerRoot =
      document.getElementById('ProductGridContainer') || document.getElementById('MainContent');
    const observer = observerRoot
      ? new MutationObserver(() => {
          if (cancelled || mutationFrame) return;
          mutationFrame = window.requestAnimationFrame(() => {
            mutationFrame = 0;
            align();
          });
        })
      : null;
    observer?.observe(observerRoot, { childList: true, subtree: true, attributes: true });
    cancellationEvents.forEach((eventName) =>
      window.addEventListener(eventName, cancel, { passive: true, once: true })
    );

    returnRestoreCleanup = () => {
      observer?.disconnect();
      window.cancelAnimationFrame(mutationFrame);
      timers.forEach((timer) => window.clearTimeout(timer));
      window.clearTimeout(focusTimer);
      window.clearTimeout(endTimer);
      cancellationEvents.forEach((eventName) => window.removeEventListener(eventName, cancel));
    };

    window.requestAnimationFrame(() => {
      if (cancelled) return;
      window.scrollTo(0, Number(snapshot.scrollY) || 0);
      window.requestAnimationFrame(align);
    });
    [80, 240, 650, 1200, 2000].forEach((delay) => {
      timers.push(window.setTimeout(align, delay));
    });
    document.fonts?.ready?.then(() => {
      if (!cancelled) align();
    });

    if (pendingSnapshot) {
      focusTimer = window.setTimeout(() => {
        const restoredElement = resolveReturnLocator(snapshot);
        const focusTarget = restoredElement?.querySelector?.('a[href]') || restoredElement;
        focusTarget?.focus?.({ preventScroll: true });
      }, 1250);
    }
    endTimer = window.setTimeout(() => {
      returnRestoreCleanup?.();
      returnRestoreCleanup = null;
    }, 2300);
  };

  const configureReturnNavigation = () => {
    on(document, 'click', captureProductReturnState, true);
    const pageShowHandler = () => window.requestAnimationFrame(restoreCapturedPosition);
    on(window, 'pageshow', pageShowHandler);
    window.requestAnimationFrame(restoreCapturedPosition);
    teardown.push(() => {
      returnRestoreCleanup?.();
      returnRestoreCleanup = null;
    });
  };

  const configureMenuCategoryAnchors = () => {
    const categoryClickHandler = (event) => {
      if (!shouldUsePhoneApp() || event.defaultPrevented || event.button !== 0) return;
      if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
      const target = event.target instanceof Element ? event.target : event.target?.parentElement;
      const anchor = target?.closest?.('.pm-toolbar-categories a[href]');
      if (!anchor || anchor.hasAttribute('download')) return;
      if (anchor.target && anchor.target.toLowerCase() !== '_self') return;

      try {
        const destination = new URL(anchor.href, window.location.origin);
        if (destination.origin !== window.location.origin) return;
        window.sessionStorage.setItem(
          menuAnchorStorageKey,
          JSON.stringify({
            version: 2,
            pathname: destination.pathname.replace(/\/+$/, '') || '/',
            timestamp: Date.now()
          })
        );
      } catch (error) {
        // Native navigation still works when private browsing blocks storage.
      }
    };

    on(document, 'click', categoryClickHandler, true);

    let marker = null;
    try {
      marker = JSON.parse(window.sessionStorage.getItem(menuAnchorStorageKey) || 'null');
      window.sessionStorage.removeItem(menuAnchorStorageKey);
    } catch (error) {
      marker = null;
    }

    const currentPath = window.location.pathname.replace(/\/+$/, '') || '/';
    if (
      !marker ||
      marker.version !== 2 ||
      marker.pathname !== currentPath ||
      Date.now() - Number(marker.timestamp || 0) > menuAnchorMaxAge
    ) {
      return;
    }

    const pendingReturn = readStoredReturn(pendingReturnStorageKey);
    const historyReturn = window.history.state?.pmMobileReturn;
    if (isFreshReturnState(pendingReturn) || isFreshReturnState(historyReturn)) return;

    let cancelled = false;
    let verticalAligned = false;
    const timers = [];
    const cancellationEvents = ['touchstart', 'pointerdown', 'wheel', 'keydown'];
    const cancel = () => {
      cancelled = true;
      timers.forEach((timer) => window.clearTimeout(timer));
      cancellationEvents.forEach((eventName) => window.removeEventListener(eventName, cancel));
    };
    const align = () => {
      if (cancelled) return;
      const toolbar = document.querySelector('.pm-collection-toolbar');
      if (!toolbar?.getClientRects().length) return;
      if (!verticalAligned) {
        const intro = document.querySelector('#MainContent > .section-purefect-menu-intro');
        const introIsHidden = !intro || window.getComputedStyle(intro).display === 'none';
        if (introIsHidden) window.scrollTo({ top: 0, left: 0, behavior: 'auto' });
        else toolbar.scrollIntoView({ block: 'start', behavior: 'auto' });
        verticalAligned = true;
      }

      const strip = toolbar.querySelector('.pm-toolbar-categories ul');
      const active = strip?.querySelector('a[aria-current="page"], a.is-active');
      if (strip && active) {
        const left = active.offsetLeft - (strip.clientWidth - active.offsetWidth) / 2;
        strip.scrollTo({ left: Math.max(0, left), behavior: 'auto' });
      }
    };

    cancellationEvents.forEach((eventName) =>
      window.addEventListener(eventName, cancel, { passive: true, once: true })
    );
    window.requestAnimationFrame(() => window.requestAnimationFrame(align));
    [100, 350, 800, 1500].forEach((delay) => timers.push(window.setTimeout(align, delay)));
    document.fonts?.ready?.then(align);
    timers.push(window.setTimeout(cancel, 1800));
    teardown.push(cancel);
  };

  const configureNavigation = () => {
    const clickHandler = (event) => {
      const more = event.target.closest('[data-pm-mobile-more-open]');
      if (more) {
        event.preventDefault();
        openDialog(document.getElementById('PmMobileMoreDialog'), more);
        return;
      }

      const search = event.target.closest('[data-pm-mobile-search-open]');
      if (search) {
        event.preventDefault();
        openDialog(document.getElementById('PmMobileSearchDialog'), search);
        return;
      }

      const close = event.target.closest('[data-pm-mobile-dialog-close]');
      if (close) {
        event.preventDefault();
        closeDialog(close.closest('.pm-mobile-sheet'));
        return;
      }

      const back = event.target.closest('[data-pm-mobile-back]');
      if (back) {
        event.preventDefault();
        const fallback = back.dataset.fallbackUrl || '/';
        const capturedReturn = returnSnapshotForCurrentProduct();
        if (capturedReturn) writeStoredReturn(pendingReturnStorageKey, capturedReturn);
        try {
          const referrer = document.referrer ? new URL(document.referrer) : null;
          if (
            window.history.length > 1 &&
            (capturedReturn || referrer?.origin === window.location.origin)
          ) {
            window.history.back();
          } else if (capturedReturn) {
            window.location.assign(capturedReturn.sourceUrl);
          } else {
            window.location.assign(fallback);
          }
        } catch (error) {
          window.location.assign(capturedReturn?.sourceUrl || fallback);
        }
        return;
      }

      const calendar = event.target.closest('[data-pm-mobile-calendar-bridge]');
      if (calendar) {
        bridgeTo('.pm-delivery-calendar__launcher', calendar);
        return;
      }

      const hub = event.target.closest('[data-pm-mobile-hub-bridge]');
      if (hub) {
        bridgeTo('.pm-hub-selector__launcher', hub);
        return;
      }

      const saved = event.target.closest('[data-pm-mobile-saved-bridge]');
      if (saved) bridgeTo('[data-saved-meals-launcher]', saved);
    };
    on(shell, 'click', clickHandler);
  };

  const productImageURL = (product) => {
    if (typeof product?.featured_image === 'string') return product.featured_image;
    return product?.featured_image?.url || product?.image || '';
  };

  const formatProductPrice = (value, currency) => {
    if (value == null || value === '') return '';
    const raw = String(value).replace(/[^0-9.-]/g, '');
    const number = Number(raw);
    if (!Number.isFinite(number)) return String(value);
    try {
      return new Intl.NumberFormat(undefined, {
        style: 'currency',
        currency: currency || 'USD'
      }).format(number);
    } catch (error) {
      return `$${number.toFixed(2)}`;
    }
  };

  const createSearchResult = (product, currency) => {
    const link = document.createElement('a');
    link.className = 'pm-mobile-search-result';
    link.href = product.url || '#';

    const imageURL = productImageURL(product);
    if (imageURL) {
      const image = document.createElement('img');
      image.src = imageURL;
      image.alt = '';
      image.width = 140;
      image.height = 140;
      image.loading = 'lazy';
      link.append(image);
    } else {
      const placeholder = document.createElement('span');
      placeholder.className = 'pm-mobile-search-result__placeholder';
      placeholder.setAttribute('aria-hidden', 'true');
      link.append(placeholder);
    }

    const copy = document.createElement('span');
    const title = document.createElement('strong');
    title.textContent = product.title || 'View dish';
    const price = document.createElement('small');
    price.textContent = formatProductPrice(product.price, currency);
    copy.append(title);
    if (price.textContent) copy.append(price);

    const arrow = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
    arrow.setAttribute('viewBox', '0 0 24 24');
    arrow.setAttribute('aria-hidden', 'true');
    const path = document.createElementNS('http://www.w3.org/2000/svg', 'path');
    path.setAttribute('d', 'm9 5 7 7-7 7');
    arrow.append(path);
    link.append(copy, arrow);
    return link;
  };

  const configureSearch = () => {
    const search = shell.querySelector('[data-pm-mobile-search]');
    const input = search?.querySelector('[data-pm-mobile-search-input]');
    const clear = search?.querySelector('[data-pm-mobile-search-clear]');
    const intro = search?.querySelector('[data-pm-mobile-search-intro]');
    const status = search?.querySelector('[data-pm-mobile-search-status]');
    const results = search?.querySelector('[data-pm-mobile-search-results]');
    const allResults = search?.querySelector('[data-pm-mobile-search-all]');
    if (!search || !input || !results || !status || !allResults) return;

    const endpoint = search.dataset.searchEndpoint || '/search/suggest.json';
    const searchURL = search.dataset.searchUrl || '/search';
    const currency = search.dataset.currency || 'USD';

    const reset = () => {
      window.clearTimeout(searchTimer);
      searchAbortController?.abort();
      searchAbortController = null;
      results.replaceChildren();
      status.textContent = '';
      intro.hidden = false;
      allResults.hidden = true;
      clear.hidden = true;
    };

    const runSearch = async () => {
      const query = input.value.trim();
      if (!query) {
        reset();
        return;
      }

      intro.hidden = true;
      clear.hidden = false;
      allResults.hidden = true;
      status.textContent = 'Searching…';
      searchAbortController?.abort();
      searchAbortController = new AbortController();

      try {
        const url = new URL(endpoint, window.location.origin);
        url.searchParams.set('q', window.PurefectSearch?.normalize(query) || query);
        url.searchParams.set('resources[type]', 'product');
        url.searchParams.set('resources[limit]', '8');
        url.searchParams.set('resources[options][fields]', 'title');
        const response = await fetch(url, {
          credentials: 'same-origin',
          headers: { Accept: 'application/json' },
          signal: searchAbortController.signal
        });
        if (!response.ok) throw new Error(`Search failed with status ${response.status}`);
        const normalizedQuery = window.PurefectSearch?.normalize(query) || query.toLocaleLowerCase();
        const payload = await response.json();
        const products = Array.from(payload?.resources?.results?.products || []);
        products.sort((left, right) => {
          const leftTitle = String(left?.title || '').toLocaleLowerCase();
          const rightTitle = String(right?.title || '').toLocaleLowerCase();
          const leftStarts = leftTitle.startsWith(normalizedQuery) ? 0 : 1;
          const rightStarts = rightTitle.startsWith(normalizedQuery) ? 0 : 1;
          if (leftStarts !== rightStarts) return leftStarts - rightStarts;
          const leftPosition = leftTitle.indexOf(normalizedQuery);
          const rightPosition = rightTitle.indexOf(normalizedQuery);
          if (leftPosition !== rightPosition) return leftPosition - rightPosition;
          return leftTitle.localeCompare(rightTitle);
        });

        const fragment = document.createDocumentFragment();
        products.forEach((product) => fragment.append(createSearchResult(product, currency)));
        results.replaceChildren(fragment);
        status.textContent = products.length
          ? `${products.length} dish${products.length === 1 ? '' : 'es'} found`
          : `No dishes match “${query}”.`;
        const fullURL = new URL(searchURL, window.location.origin);
        fullURL.searchParams.set('q', window.PurefectSearch?.normalize(query) || query);
        fullURL.searchParams.set('type', 'product');
        allResults.href = `${fullURL.pathname}${fullURL.search}`;
        allResults.hidden = products.length === 0;
      } catch (error) {
        if (error.name === 'AbortError') return;
        results.replaceChildren();
        status.textContent = 'Search is taking a break. Please try again.';
      }
    };

    const inputHandler = () => {
      window.clearTimeout(searchTimer);
      if (!input.value.trim()) {
        reset();
        return;
      }
      clear.hidden = false;
      searchTimer = window.setTimeout(runSearch, 140);
    };

    const clearHandler = () => {
      input.value = '';
      reset();
      input.focus();
    };

    on(input, 'input', inputHandler);
    on(clear, 'click', clearHandler);
    teardown.push(reset);
  };

  const syncHubName = () => {
    const source = document.querySelector('.pm-hub-selector [data-pm-hub-name]');
    const target = shell.querySelector('[data-pm-mobile-hub-name]');
    if (source && target) target.textContent = source.textContent.trim() || 'Choose your location';
  };

  const observeHub = () => {
    syncHubName();
    const source = document.querySelector('.pm-hub-selector [data-pm-hub-name]');
    if (!source) return;
    const observer = new MutationObserver(syncHubName);
    observer.observe(source, { childList: true, subtree: true, characterData: true });
    teardown.push(() => observer.disconnect());
  };

  const setCartCount = (count) => {
    const badge = shell.querySelector('[data-pm-mobile-cart-count]');
    if (!badge) return;
    const safeCount = Math.max(0, Number(count) || 0);
    badge.textContent = safeCount > 99 ? '99+' : String(safeCount);
    badge.hidden = safeCount === 0;
    shell.dataset.cartCount = String(safeCount);
  };

  const refreshCartCount = async () => {
    try {
      const route = window.routes?.cart_url || '/cart';
      const response = await fetch(`${route}.js`, {
        credentials: 'same-origin',
        headers: { Accept: 'application/json' }
      });
      if (!response.ok) return;
      const cart = await response.json();
      setCartCount(cart.item_count);
    } catch (error) {
      // Keep the last confirmed count if the network is temporarily unavailable.
    }
  };

  const configureCartCount = () => {
    setCartCount(shell.dataset.cartCount);

    const subscribeToEvent =
      typeof window.subscribe === 'function'
        ? window.subscribe
        : (typeof subscribe === 'function' ? subscribe : null);
    const events =
      window.PUB_SUB_EVENTS ||
      (typeof PUB_SUB_EVENTS !== 'undefined' ? PUB_SUB_EVENTS : null);
    if (subscribeToEvent && events?.cartUpdate) {
      const unsubscribe = subscribeToEvent(events.cartUpdate, refreshCartCount);
      if (typeof unsubscribe === 'function') teardown.push(unsubscribe);
    }

    const updateHandler = () => window.setTimeout(refreshCartCount, 120);
    on(document, 'cart:refresh', updateHandler);
    on(document, 'pm:cart-updated', updateHandler);
  };

  const compactText = (value) => String(value || '').replace(/\s+/g, ' ').trim();

  const visibleProductPrice = () => {
    const priceRoot = document.querySelector('product-info [id^="price-"]');
    if (!priceRoot) return null;
    return (
      priceRoot.querySelector('.price--on-sale .price-item--sale') ||
      priceRoot.querySelector('.price__sale .price-item--sale') ||
      priceRoot.querySelector('.price-item--regular') ||
      priceRoot.querySelector('.price-item')
    );
  };

  const productActionPrice = (priceElement) => {
    if (!priceElement) return '';
    const productInfo = document.querySelector('product-info[id^="MainProduct-"]');
    const finishingTouches = productInfo?.querySelector('[data-pm-finishing-touches]');
    const extrasCents = Math.max(0, Number(finishingTouches?.dataset.selectedPriceCents || 0));
    let selectedVariant = null;
    try {
      selectedVariant = JSON.parse(productInfo?.querySelector('[data-selected-variant]')?.textContent || 'null');
    } catch (error) {
      selectedVariant = null;
    }
    const baseCents = Number(selectedVariant?.price ?? productInfo?.dataset.productPriceCents);
    const quantity = Math.max(1, Number(productInfo?.querySelector('input[name="quantity"]')?.value || 1));
    if (!Number.isFinite(baseCents)) return compactText(priceElement.textContent);

    const locale = finishingTouches?.dataset.locale || document.documentElement.lang || 'en-US';
    const currency = finishingTouches?.dataset.currency || productInfo?.dataset.productCurrency || 'USD';
    try {
      return new Intl.NumberFormat(locale, {
        style: 'currency',
        currency,
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      }).format(((baseCents + extrasCents) * quantity) / 100);
    } catch (error) {
      return '$' + (((baseCents + extrasCents) * quantity) / 100).toFixed(2);
    }
  };

  const firstIncompleteChoice = () => {
    const productInfo = document.querySelector('product-info[id^="MainProduct-"]');
    if (!productInfo) return null;
    const sidePickers = Array.from(productInfo.querySelectorAll('purefect-side-picker'));
    const missingSide = sidePickers.find(
      (picker) => !picker.querySelector('input[data-pm-real-side="true"]:checked')
    );
    if (missingSide) return missingSide;

    const requiredSelect = Array.from(productInfo.querySelectorAll('select[required]')).find((select) => !select.value);
    if (requiredSelect) return requiredSelect;

    const invalid = productInfo.querySelector('[aria-invalid="true"], .is-invalid, [data-invalid="true"]');
    return invalid || productInfo.querySelector('[data-pm-meal-builder], variant-selects, variant-radios');
  };

  const revealChoice = (target) => {
    if (!target) return;
    const details = target.matches('details') ? target : target.closest('details') || target.querySelector?.('details');
    if (details) details.open = true;
    target.scrollIntoView({
      behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth',
      block: 'center'
    });
    const focusTarget =
      target.matches('button, input, select, summary, a') ? target : target.querySelector?.('summary, input, select, button');
    window.setTimeout(() => focusTarget?.focus({ preventScroll: true }), 260);
  };

  const configureActionTray = () => {
    const tray = shell.querySelector('[data-pm-mobile-action-tray]');
    const action = tray?.querySelector('[data-pm-mobile-action-button]');
    const actionLabel = tray?.querySelector('[data-pm-mobile-action-label]');
    const actionValue = tray?.querySelector('[data-pm-mobile-action-value]');
    const actionKicker = tray?.querySelector('[data-pm-mobile-action-kicker]');
    const mode = tray?.dataset.mode;
    if (!tray || !action || !mode) return;

    let syncFrame = 0;
    const nativeFocusState = new Map();
    const removeNativeFocus = (element) => {
      if (!(element instanceof HTMLElement) || nativeFocusState.has(element)) return;
      nativeFocusState.set(element, {
        tabindex: element.getAttribute('tabindex'),
        ariaHidden: element.getAttribute('aria-hidden')
      });
      element.setAttribute('tabindex', '-1');
      element.setAttribute('aria-hidden', 'true');
    };
    const scheduleSync = () => {
      if (syncFrame) return;
      syncFrame = window.requestAnimationFrame(() => {
        syncFrame = 0;
        sync();
      });
    };

    const sync = () => {
      if (mode === 'product') {
        const source = document.querySelector(
          'product-info[id^="MainProduct-"] button.product-form__submit[id^="ProductSubmitButton-"]'
        );
        const price = visibleProductPrice();
        if (!source) {
          tray.hidden = true;
          document.body.classList.remove('pm-mobile-has-action-tray');
          return;
        }
        // Keep the inline purchase button keyboard-accessible as well as the tray.
        document
          .querySelectorAll('product-info[id^="MainProduct-"] .shopify-payment-button button, product-info[id^="MainProduct-"] .shopify-payment-button a')
          .forEach(removeNativeFocus);
        const purchaseRect = source.getBoundingClientRect();
        const purchaseVisible = purchaseRect.top < window.innerHeight - 90 && purchaseRect.bottom > 140;
        const overlayOpen = document.querySelector('dialog[open], quick-add-modal[open], cart-drawer.active');
        tray.hidden = purchaseVisible || !!overlayOpen;
        document.body.classList.toggle('pm-mobile-has-action-tray', !tray.hidden);
        const sourceLabel = compactText(source.querySelector('span')?.textContent || source.textContent);
        const loading = source.classList.contains('loading');
        const unavailable = source.disabled && /sold out|unavailable/i.test(sourceLabel);
        const needsChoices = source.disabled && !unavailable && !loading;
        action.disabled = unavailable || loading;
        action.dataset.choiceRequired = String(needsChoices);
        action.classList.toggle('loading', loading);
        actionLabel.textContent = loading ? 'Adding…' : needsChoices ? 'Finish choices' : sourceLabel || 'Add to cart';
        if (price && actionValue) actionValue.textContent = productActionPrice(price);
        if (actionKicker) {
          actionKicker.textContent = needsChoices ? 'Complete your meal' : 'Current total';
        }
        return;
      }

      if (mode === 'cart') {
        const source = document.querySelector('#checkout.cart__checkout-button');
        const total = document.querySelector('.cart__footer .totals__total-value');
        const count = Number(shell.dataset.cartCount || 0);
        if (!source || count === 0) {
          tray.hidden = true;
          document.body.classList.remove('pm-mobile-has-action-tray');
          return;
        }
        tray.hidden = false;
        document.body.classList.add('pm-mobile-has-action-tray');
        action.disabled = source.disabled;
        actionLabel.textContent = compactText(source.textContent) || 'Checkout';
        if (total && actionValue) actionValue.textContent = compactText(total.textContent);
      }
    };

    const clickHandler = () => {
      const source = mode === 'product'
        ? document.querySelector('product-info[id^="MainProduct-"] button.product-form__submit[id^="ProductSubmitButton-"]')
        : document.querySelector('#checkout.cart__checkout-button');

      if (!source || source.disabled) {
        if (mode === 'product') {
          revealChoice(firstIncompleteChoice());
          emitToast('Choose the required options to finish your meal.');
        }
        return;
      }
      source.click();
    };

    on(action, 'click', clickHandler);
    on(window, 'scroll', scheduleSync, { passive: true });
    on(window, 'resize', scheduleSync, { passive: true });
    on(document, 'pm:finishing-touches:change', scheduleSync);
    on(document, 'change', (event) => {
      if (event.target.matches?.('product-info[id^="MainProduct-"] input[name="quantity"]')) scheduleSync();
    });
    const observedRoot = document.getElementById('MainContent');
    if (observedRoot) {
      const observer = new MutationObserver(scheduleSync);
      observer.observe(observedRoot, {
        attributes: true,
        childList: true,
        subtree: true,
        characterData: true,
        attributeFilter: ['disabled', 'class', 'aria-disabled']
      });
      teardown.push(() => observer.disconnect());
    }
    sync();
    teardown.push(() => {
      window.cancelAnimationFrame(syncFrame);
      nativeFocusState.forEach((state, element) => {
        if (state.tabindex == null) element.removeAttribute('tabindex');
        else element.setAttribute('tabindex', state.tabindex);
        if (state.ariaHidden == null) element.removeAttribute('aria-hidden');
        else element.setAttribute('aria-hidden', state.ariaHidden);
      });
      nativeFocusState.clear();
      document.body.classList.remove('pm-mobile-has-action-tray');
    });
  };

  const configureMealCarousels = () => {
    document.querySelectorAll('#MainContent .pm-home .pm-meal-grid').forEach((rail, railIndex) => {
      if (rail.closest('[data-pm-mobile-meal-rail]')) return;
      const parent = rail.parentNode;
      if (!parent) return;

      const wrapper = document.createElement('div');
      wrapper.className = 'pm-mobile-meal-rail';
      wrapper.dataset.pmMobileMealRail = '';
      const controls = document.createElement('div');
      controls.className = 'pm-mobile-meal-rail__controls';
      controls.setAttribute('aria-label', 'Menu carousel controls');

      const createArrow = (direction) => {
        const button = document.createElement('button');
        button.type = 'button';
        button.className = 'pm-mobile-meal-rail__arrow pm-mobile-meal-rail__arrow--' + direction;
        button.dataset.pmMealRailDirection = direction;
        button.setAttribute(
          'aria-label',
          direction === 'previous' ? 'Previous menu dish' : 'Next menu dish'
        );
        button.setAttribute('aria-controls', rail.id || 'PmMealRail-' + (railIndex + 1));
        button.innerHTML =
          direction === 'previous'
            ? '<svg viewBox="0 0 24 24" aria-hidden="true"><path d="m15 5-7 7 7 7"></path></svg>'
            : '<svg viewBox="0 0 24 24" aria-hidden="true"><path d="m9 5 7 7-7 7"></path></svg>';
        return button;
      };

      if (!rail.id) rail.id = 'PmMealRail-' + (railIndex + 1);
      const previous = createArrow('previous');
      const next = createArrow('next');
      controls.append(previous, next);
      parent.insertBefore(wrapper, rail);
      wrapper.append(rail, controls);

      const cards = () => Array.from(rail.querySelectorAll('.pm-meal-card'));
      const positions = () => {
        const items = cards();
        const origin = items[0]?.offsetLeft || 0;
        return items.map((card) => Math.max(0, card.offsetLeft - origin));
      };
      const update = () => {
        const maxScroll = Math.max(0, rail.scrollWidth - rail.clientWidth);
        const scrollable = maxScroll > 2;
        controls.hidden = !scrollable;
        previous.hidden = !scrollable || rail.scrollLeft <= 2;
        next.hidden = !scrollable || rail.scrollLeft >= maxScroll - 2;
      };
      const move = (direction) => {
        const stops = positions();
        if (!stops.length) return;
        const current = rail.scrollLeft;
        const target =
          direction === 'next'
            ? stops.find((position) => position > current + 4) ?? stops[stops.length - 1]
            : [...stops].reverse().find((position) => position < current - 4) ?? stops[0];
        rail.scrollTo({
          left: target,
          behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth'
        });
      };
      const previousHandler = () => move('previous');
      const nextHandler = () => move('next');
      previous.addEventListener('click', previousHandler);
      next.addEventListener('click', nextHandler);
      rail.addEventListener('scroll', update, { passive: true });
      const resizeObserver = typeof ResizeObserver === 'function' ? new ResizeObserver(update) : null;
      resizeObserver?.observe(rail);
      window.requestAnimationFrame(update);

      teardown.push(() => {
        previous.removeEventListener('click', previousHandler);
        next.removeEventListener('click', nextHandler);
        rail.removeEventListener('scroll', update);
        resizeObserver?.disconnect();
        if (wrapper.isConnected) {
          wrapper.parentNode.insertBefore(rail, wrapper);
          wrapper.remove();
        }
      });
    });
  };

  // Use native fixed positioning on iOS; do not chase momentum scrolling with layout writes.

  const initialize = () => {
    if (!shouldUsePhoneApp() || shell.dataset.mobileReady === 'true') return;
    document.documentElement.classList.add('pm-mobile-app-active');
    configureDialogs();
    configureReturnNavigation();
    configureMenuCategoryAnchors();
    configureNavigation();
    configureSearch();
    configureCartCount();
    observeHub();
    configureMealCarousels();
    configureActionTray();

    shell.dataset.mobileReady = 'true';
  };

  const destroy = () => {
    if (shell.dataset.mobileReady !== 'true') return;
    if (activeDialog?.open) closeDialog(activeDialog, false);
    window.clearTimeout(searchTimer);
    window.clearTimeout(toastTimer);
    searchAbortController?.abort();
    searchAbortController = null;
    teardown.splice(0).forEach((callback) => {
      try {
        callback();
      } catch (error) {
        // Teardown should never prevent the desktop/tablet theme from returning.
      }
    });
    document.body.classList.remove(
      'pm-mobile-sheet-open',
      'pm-mobile-has-action-tray',
      'pm-mobile-filter-open'
    );
    document.documentElement.classList.remove('pm-mobile-app-active');
    delete shell.dataset.mobileReady;
  };

  const handleMediaChange = () => {
    if (shouldUsePhoneApp()) initialize();
    else destroy();
  };

  if (typeof phoneQuery.addEventListener === 'function') {
    phoneQuery.addEventListener('change', handleMediaChange);
  } else {
    phoneQuery.addListener(handleMediaChange);
  }

  handleMediaChange();
})();
